import { useCallback, useEffect, useRef, useState, type MouseEvent, type TouchEvent } from "react";
import { holdHaptic } from "@cntrl/native";

/** How long a finger has to stay down, and how far it may drift, before a touch counts as a HOLD. */
const HOLD_MS = 450;
const MOVE_TOLERANCE_PX = 10;
/** How long a finger must stay down before the element LOOKS pressed — so an ordinary tap never flickers the
 *  hold effect (iOS waits about this long before highlighting too). The CSS builds over the rest of HOLD_MS. */
export const PRESS_FEEDBACK_DELAY_MS = 120;
/** Two taps this close in time and space are a double-tap (ART-264: double-tap a message to heart it). */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP_PX = 30;

/**
 * A press-and-hold on a touch screen (ART-264) — how a phone opens a message's actions, where a desktop hovers
 * for the `…` trigger. Touch only: a mouse never starts it.
 *
 * Three things it has to get right, each of which is a bug when missed:
 * - **A scroll is not a hold.** The finger that scrolls the conversation lands on a bubble too; moving past a
 *   few pixels cancels, so flicking through a chat never opens a sheet.
 * - **The tap that ENDS a hold is swallowed.** Lifting the finger fires a click, which on a photo would also
 *   open the image under the sheet that just appeared — so the next click after a hold is cancelled.
 * - **The platform's own long-press is suppressed** — the `contextmenu` event (Android, desktop emulation) is
 *   prevented here, and iOS's selection callout is turned off in CSS by the caller (`-webkit-touch-callout`).
 * - **…and not just on the held element (ART-270).** The sheet a hold opens appears UNDER the finger that is still
 *   down, so iOS carries its own long-press on into whatever is now there and selected the whole PAGE, with the
 *   Copy / Look Up callout over it — the caller's `user-select: none` covers only its own box. So for as long as
 *   a press is down, `selectstart` is refused document-wide, and any selection that started anyway is cleared
 *   the moment the hold fires.
 * - **Only events that START inside the held element count.** The sheet a hold opens is portalled to <body>, but
 *   React bubbles its events through the COMPONENT tree — so a tap on the sheet's own button reached this
 *   element's `onClickCapture` and was swallowed as "the click that ends a hold", and a press on the sheet would
 *   start another hold. Every handler checks the DOM (`currentTarget.contains(target)`) first.
 *
 * **It says when a hold has started and when it takes (ART-269).** The returned props carry `data-pressing` from
 * `PRESS_FEEDBACK_DELAY_MS` after a finger lands until it lifts, moves, or the hold fires, so the caller can darken
 * the element as the hold builds — otherwise nothing tells you a hold is under way until the sheet appears. The
 * short delay keeps a plain TAP from flashing the effect. And the hold firing is marked by a haptic tap, the way
 * an iOS context menu is.
 *
 * **It also hears a DOUBLE-TAP** (`onDoubleTap`, ART-264): two quick taps in the same place. Only a clean tap
 * counts — one that turned into a hold or a scroll does not — so a hold never doubles as half of one.
 */
export function useLongPress(onHold: () => void, enabled = true, onDoubleTap?: () => void) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const held = useRef(false);
  const onHoldRef = useRef(onHold);
  onHoldRef.current = onHold;
  const [pressing, setPressing] = useState(false);
  const pressTimer = useRef<number | null>(null);
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null);
  const onDoubleTapRef = useRef(onDoubleTap);
  onDoubleTapRef.current = onDoubleTap;

  // Refuses any text selection while a press is down — see the fourth point above.
  const blockSelect = useRef<((e: Event) => void) | null>(null);
  const releaseSelect = useCallback(() => {
    if (blockSelect.current) document.removeEventListener("selectstart", blockSelect.current, true);
    blockSelect.current = null;
  }, []);

  const cancel = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    if (pressTimer.current !== null) window.clearTimeout(pressTimer.current);
    pressTimer.current = null;
    start.current = null;
    setPressing(false);
    releaseSelect();
  }, [releaseSelect]);

  useEffect(() => cancel, [cancel]);

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled || e.touches.length !== 1 || !isInside(e)) return;
      const t = e.touches[0];
      start.current = { x: t.clientX, y: t.clientY };
      held.current = false;
      pressTimer.current = window.setTimeout(() => {
        pressTimer.current = null;
        setPressing(true);
      }, PRESS_FEEDBACK_DELAY_MS);
      releaseSelect();
      blockSelect.current = (ev: Event) => ev.preventDefault();
      document.addEventListener("selectstart", blockSelect.current, true);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        held.current = true;
        setPressing(false);
        window.getSelection()?.removeAllRanges();
        holdHaptic();
        onHoldRef.current();
      }, HOLD_MS);
    },
    [enabled, releaseSelect],
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      const s = start.current;
      if (!s) return;
      const t = e.touches[0];
      if (Math.abs(t.clientX - s.x) > MOVE_TOLERANCE_PX || Math.abs(t.clientY - s.y) > MOVE_TOLERANCE_PX) cancel();
    },
    [cancel],
  );

  // A clean tap ends with the press still live (no hold fired, no move cancelled it): that is the only kind that
  // can be half of a double-tap.
  const onTouchEnd = useCallback(
    (e: TouchEvent) => {
      const s = start.current;
      const clean = s !== null && !held.current && isInside(e);
      cancel();
      if (!clean || !onDoubleTapRef.current) return;
      const now = Date.now();
      const prev = lastTap.current;
      if (prev && now - prev.at < DOUBLE_TAP_MS && Math.hypot(s.x - prev.x, s.y - prev.y) < DOUBLE_TAP_SLOP_PX) {
        lastTap.current = null;
        onDoubleTapRef.current();
      } else {
        lastTap.current = { at: now, x: s.x, y: s.y };
      }
    },
    [cancel],
  );

  const onClickCapture = useCallback((e: MouseEvent) => {
    if (!held.current || !isInside(e)) return;
    held.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onContextMenu = useCallback(
    (e: MouseEvent) => {
      if (enabled) e.preventDefault();
    },
    [enabled],
  );

  if (!enabled) return {};
  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: cancel,
    onClickCapture,
    onContextMenu,
    "data-pressing": pressing || undefined,
  };
}

/** True when the event's target is in this element's own DOM — not in a portalled overlay React bubbled it from. */
function isInside(e: { currentTarget: EventTarget; target: EventTarget }): boolean {
  return e.currentTarget instanceof Node && e.target instanceof Node && e.currentTarget.contains(e.target);
}
